// Joins persisted cart lines onto the live catalogue so price, image and
// stock always reflect the current data - the cart itself stores none of
// that (see cartStore.ts), both because prices can change and because a
// ProductImage.url is a short-lived signed S3 URL that must be read fresh.

import { useMemo } from 'react'
import { useCartStore, lineKey, type CartItem } from '../../store/cartStore'
import type { Catalogue } from './catalogue'
import { availableFor, priceOf, variantLabel } from './pricing'
import { primaryImage } from './format'

export interface CartDetailLine extends CartItem {
  key: string
  variantName: string | null
  unitPrice: number
  lineTotal: number
  image: string | null
  /** Live available stock for this line's SKU. */
  max: number
  /** Effective per-unit weight (variant override, else the product's), for weight-tiered shipping. */
  weightGrams: number
  currency: string
  /** The product or variant this line pointed at no longer exists. */
  orphaned: boolean
}

export interface CartDetail {
  lines: CartDetailLine[]
  subtotal: number
  /** Sum of each line's weight × quantity, for weight-tiered shipping. */
  totalWeightGrams: number
  currency: string
  hasOrphans: boolean
}

export function useCartDetail(catalogue: Catalogue): CartDetail {
  const items = useCartStore((s) => s.items)

  return useMemo(() => {
    const lines = items.map((item): CartDetailLine => {
      const product = catalogue.products.find((p) => p.id === item.productId) ?? null
      const variant = item.variantId
        ? product?.variants.find((v) => v.id === item.variantId) ?? null
        : null
      const orphaned = !product || (!!item.variantId && !variant)
      const unitPrice = product && !orphaned ? priceOf(product, variant) : 0
      return {
        ...item,
        key: lineKey(item),
        variantName: variant ? variantLabel(variant) : null,
        unitPrice,
        lineTotal: unitPrice * item.quantity,
        image: product ? primaryImage(product)?.url ?? null : null,
        max: product && !orphaned ? availableFor(product, catalogue.stockBySku, variant) : 0,
        weightGrams: variant?.weightGrams ?? product?.weightGrams ?? 0,
        currency: product?.currency ?? 'DKK',
        orphaned,
      }
    })
    return {
      lines,
      subtotal: lines.reduce((sum, l) => sum + l.lineTotal, 0),
      totalWeightGrams: lines.reduce((sum, l) => sum + l.weightGrams * l.quantity, 0),
      currency: lines[0]?.currency ?? 'DKK',
      hasOrphans: lines.some((l) => l.orphaned),
    }
  }, [items, catalogue])
}
